import {
  CommonArea,
  Community,
  Incident,
  Payment,
  User,
} from '../types';
import {
  getCommunityById,
  getAllCommunitiesWithMembers,
} from '@/services/communityService';
import {
  getReservationAreasByCommunity,
  getReservationAreasCount,
  getReservationAreasWithCommunities,
} from '@/services/reservationAreaService';
import { getCommunityPaymentRecords } from '@/services/paymentService';
import { getAllIncidents, getIncidentsByCommunity } from '@/services/incidentService';
import {
  cloneOldSlotsByDay,
  cloneSlotsByDay,
  mapSlotsConfigToSnapshot,
  type ReservationAreaSlotsConfig,
} from '@/utils/commonAreaSlots';
import { resolveAccessibleCommunityIds, resolvePrimaryCommunityId } from '@/utils/communityScope';

type LoadedCommunity = Awaited<ReturnType<typeof getAllCommunitiesWithMembers>>[number];
type ReservationAreaWithCommunities = Awaited<ReturnType<typeof getReservationAreasWithCommunities>>[number];
type ReservationAreaByCommunity = Awaited<ReturnType<typeof getReservationAreasByCommunity>>[number];

type CommonAreaSource = {
  id: string;
  name: string;
  description?: string | null;
  max_reservations_per_day?: number | null;
  max_future_days?: number | null;
  abs_limit?: boolean | null;
  unique_house?: boolean | null;
  blocked_days: string[] | null;
  reservation_payment?: boolean | null;
  only_renter?: boolean | null;
  lights?: boolean | null;
  ssid?: string | null;
  slots_config?: ReservationAreaSlotsConfig | null;
  community_ids?: string[];
};

const sortCommunitiesByName = <T extends { name: string }>(items: T[]) =>
  [...items].sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));

const toDataCommunity = (community: LoadedCommunity): Community => ({
  id: community.id,
  name: community.name,
  address: community.address,
  city: community.city,
  cp: community.cp,
  cif: community.cif,
  adminId: '',
  members: community.members,
  commonAreas: [],
  incidents: [],
  payments: [],
  polls: [],
  blocks: community.blocks,
  floors: community.floors,
  letters: community.letters,
  secret: community.secret,
  showUpdates: community.showUpdates,
  availableSections: community.availableSections,
});

const mapCommonArea = (
  area: CommonAreaSource,
  communityId: string,
  communityIds: string[],
): CommonArea => {
  const slotSnapshot = mapSlotsConfigToSnapshot(area.slots_config);

  return {
    id: area.id,
    name: area.name,
    description: area.description ?? undefined,
    communityId,
    communityIds,
    maxReservationsPerDay: area.max_reservations_per_day ?? undefined,
    maxFutureDays: area.max_future_days ?? null,
    absLimit: area.abs_limit ?? false,
    uniqueHouse: area.unique_house ?? false,
    slotsByDay: cloneSlotsByDay(slotSnapshot.slotsByDay),
    oldSlotsByDay: cloneOldSlotsByDay(slotSnapshot.oldSlotsByDay),
    slotsApplyDate: slotSnapshot.slotsApplyDate,
    blockedDays: area.blocked_days ?? [],
    reservationPayment: area.reservation_payment === true,
    onlyRenter: area.only_renter ?? false,
    lights: area.lights ?? false,
    ssid: area.ssid ?? null,
  };
};

export const fetchCommunitiesForUser = async (currentUser: User | null): Promise<Community[]> => {
  if (!currentUser) {
    return [];
  }

  const dbCommunities = await getAllCommunitiesWithMembers();
  return sortCommunitiesByName(dbCommunities.map(toDataCommunity));
};

export const fetchPaymentsForCommunities = async (
  currentUser: User | null,
  communities: Community[],
): Promise<Payment[]> => {
  if (!currentUser) {
    return [];
  }

  const communityIds = communities
    .map((community) => community.id)
    .filter((id): id is string => Boolean(id));

  if (communityIds.length === 0) {
    return [];
  }

  return getCommunityPaymentRecords({ communityIds });
};

export const fetchCommonAreasForUser = async (
  currentUser: User | null,
): Promise<{ areas: CommonArea[]; totalCount: number }> => {
  if (!currentUser) {
    return { areas: [], totalCount: 0 };
  }

  const primaryCommunityId = resolvePrimaryCommunityId(currentUser);
  const isGlobalAdmin = currentUser.role === 'admin' && !primaryCommunityId;

  if (isGlobalAdmin) {
    const [rows, total] = await Promise.all([
      getReservationAreasWithCommunities(),
      getReservationAreasCount(),
    ]);

    const aggregated = rows.flatMap((row: ReservationAreaWithCommunities) => {
      const communityIds = (row.community_zone ?? [])
        .map((link) => link?.community_id)
        .filter((id): id is string => Boolean(id));

      if (communityIds.length === 0) {
        return [mapCommonArea(row, '', [])];
      }

      const uniqueCommunityIds = Array.from(new Set(communityIds));
      return uniqueCommunityIds.map((communityId) => mapCommonArea(row, communityId, uniqueCommunityIds));
    });

    return { areas: aggregated, totalCount: total };
  }

  const accessibleCommunityIds = resolveAccessibleCommunityIds(currentUser);
  if (accessibleCommunityIds.length === 0) {
    return { areas: [], totalCount: 0 };
  }

  const areasByCommunity = await Promise.all(
    accessibleCommunityIds.map(async (communityId) => ({
      communityId,
      areas: await getReservationAreasByCommunity(communityId),
    })),
  );

  const areas = areasByCommunity.flatMap(({ communityId, areas: rawAreas }) =>
    rawAreas.map((area: ReservationAreaByCommunity) =>
      mapCommonArea(area, communityId, area.community_ids ?? [communityId]),
    ),
  );

  return { areas, totalCount: areas.length };
};

export const fetchMissingPrimaryCommunity = async (
  currentUser: User | null,
  communities: Community[],
): Promise<Community | null> => {
  const primaryCommunityId = resolvePrimaryCommunityId(currentUser);
  if (!primaryCommunityId) {
    return null;
  }

  const exists = communities.some((community) => community.id === primaryCommunityId);
  if (exists) {
    return null;
  }

  const dbCommunity = await getCommunityById(primaryCommunityId);
  if (!dbCommunity) {
    return null;
  }

  return {
    id: dbCommunity.id,
    name: dbCommunity.name,
    address: dbCommunity.address ?? '',
    city: dbCommunity.city ?? '',
    cp: dbCommunity.cp ?? '',
    cif: dbCommunity.cif ?? '',
    adminId: '',
    members: [],
    commonAreas: [],
    incidents: [],
    payments: [],
    polls: [],
    blocks: dbCommunity.blocks,
    floors: dbCommunity.floors,
    letters: dbCommunity.letters,
    secret: dbCommunity.secret,
    showUpdates: dbCommunity.showUpdates,
    availableSections: dbCommunity.availableSections,
  };
};

export const fetchIncidentsForUser = async (currentUser: User | null): Promise<Incident[]> => {
  if (!currentUser) {
    return [];
  }

  if (currentUser.role === 'admin') {
    return getAllIncidents();
  }

  const accessibleCommunityIds = resolveAccessibleCommunityIds(currentUser);
  if (!accessibleCommunityIds.length) {
    return [];
  }

  const incidentsByCommunity = await Promise.all(
    accessibleCommunityIds.map((communityId) => getIncidentsByCommunity(communityId)),
  );

  return Array.from(
    new Map(incidentsByCommunity.flat().map((incident) => [incident.id, incident])).values(),
  );
};
